import * as React from 'react';
import { useInfiniteQuery } from 'react-query';

export interface ContinuationResponse<T> {
    rows: T[];
    continuationToken?: string;
}

interface UseServerPaginationOptions<T> {
    queryKey: unknown[];
    fetchPage: (token?: string, pageSize?: number) => Promise<ContinuationResponse<T>>;
    pageSize: number;
}

export function useServerPagination<T>({
    queryKey,
    fetchPage,
    pageSize: initialPageSize
}: UseServerPaginationOptions<T>) {
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(initialPageSize);

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteQuery(
        queryKey,
        ({ pageParam }) => fetchPage(pageParam, pageSize),
        {
            getNextPageParam: lastPage =>
                lastPage.continuationToken ?? undefined,
            staleTime: Infinity,
        }
    );

    // if the next page has no results, go to the previous page
    const prevPageCountRef = React.useRef(0);
    React.useEffect(() => {
        const pageCount = data?.pages.length ?? 0;

        // only run when a new page is fetched
        if (pageCount > prevPageCountRef.current) {
            const lastPage = data!.pages[pageCount - 1];

            if (lastPage.rows.length === 0) {
                setCurrentPage(prev => Math.max(1, prev - 1));
            }
        }

        prevPageCountRef.current = pageCount;
    }, [data]);

    const allRows = React.useMemo(
        () => data?.pages.flatMap(p => p.rows) ?? [],
        [data]
    );

    const pagedData = React.useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return allRows.slice(start, start + pageSize);
    }, [allRows, currentPage, pageSize]);

    const onPageChange = (page: number, nextPageSize?: number) => {
        const effectivePageSize = Number(nextPageSize ?? pageSize);
        const isSizeChange = nextPageSize !== undefined && effectivePageSize !== pageSize;
        const nextPage = isSizeChange ? 1 : page;
        const needsMoreData = nextPage * effectivePageSize > allRows.length;

        if (isSizeChange) {
            setPageSize(effectivePageSize);
        }

        if (needsMoreData && hasNextPage) {
            fetchNextPage();
        }

        setCurrentPage(nextPage);
    };

    return {
        data: pagedData,
        isLoading: isLoading || isFetchingNextPage,
        isError,
        currentPage,
        pageSize,
        total: hasNextPage ? allRows.length + 1 : allRows.length,
        onPageChange
    };
}
